import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Users, 
  Crown, 
  Copy, 
  Check, 
  Play, 
  UserCheck,
  UserX,
  ArrowLeft,
  X,
  Settings,
  Timer,
  Target,
  Sparkles,
  Shield,
  Zap,
  Trophy,
  Edit,
  Save
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { realtimeService } from '@/services/realtime'
import { databaseService } from '@/services/database'
import { Room, Player } from '@/types'
import { trackEvent } from '@/services/analytics'
import { useTranslation } from 'react-i18next'
import RoomClosedModal from '@/components/RoomClosedModal'

export default function LobbyPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [room, setRoom] = useState<Room | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null)
  const [onlinePlayers, setOnlinePlayers] = useState<Player[]>([])
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [kickingPlayer, setKickingPlayer] = useState<Player | null>(null)
  const [editingSettings, setEditingSettings] = useState(false)
  const [editedSettings, setEditedSettings] = useState({
    maxPlayers: 6,
    maxRounds: 3,
    promptTimeLimit: 300,
    votingTimeLimit: 180
  })
  const [editedGameMode, setEditedGameMode] = useState<'word_to_prompt' | 'user_word_to_prompt'>('word_to_prompt')
  const [showRoomClosed, setShowRoomClosed] = useState(false)

  const amIHost = () => {
    try {
      if (currentPlayer && typeof currentPlayer.isHost === 'boolean') return !!currentPlayer.isHost
      const saved = localStorage.getItem('currentPlayer')
      if (!saved) return false
      const me = JSON.parse(saved)
      return !!me?.isHost
    } catch {
      return !!currentPlayer?.isHost
    }
  }

  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    // Get current player from localStorage
    const savedPlayer = localStorage.getItem('currentPlayer')
    if (savedPlayer) {
      setCurrentPlayer(JSON.parse(savedPlayer))
    }

    loadRoom()
    const cleanupRealtime = setupRealtimeSubscription()
    setupPresence()
    
    // Cleanup function
    return () => {
      if (cleanupRealtime) {
        cleanupRealtime()
      }
      // Note: Don't cleanup room here as it causes issues with rapid page transitions
      // Cleanup will happen when user actually leaves the room via buttons
    }
  }, [roomId])

  const loadRoom = async () => {
    try {
      const roomData = await databaseService.getRoom(roomId!)
      // Oda bitmişse (host ayrılmış olabilir), kullanıcıya modal göster
      if (roomData.status === 'finished') {
        setRoom(roomData)
        setShowRoomClosed(true)
        return
      }
      setRoom(roomData)
      // Update edited settings when room loads
      setEditedSettings({
        maxPlayers: roomData.maxPlayers,
        maxRounds: roomData.maxRounds,
        promptTimeLimit: roomData.settings.promptTimeLimit,
        votingTimeLimit: roomData.settings.votingTimeLimit
      })
      setEditedGameMode(roomData.gameMode)
    } catch (error: any) {
      setError(error.message || 'Oda yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`room-${roomId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'rooms',
        filter: `id=eq.${roomId}`
      }, (payload) => {
        const dbRoom: any = (payload as any).new
        // Oda silinmiş ya da bitmişse modal göster (host hariç)
        if (!dbRoom || dbRoom.status === 'finished') {
          if (!amIHost()) setShowRoomClosed(true)
          return
        }
        // Navigate players into the correct screen based on mode/status
        if (dbRoom) {
          console.log('Room status changed via database:', dbRoom.status, 'game_mode:', dbRoom.game_mode)
          
          if (dbRoom.game_mode === 'user_word_to_prompt' && dbRoom.status === 'waiting') {
            console.log('Navigating to user word page')
            navigate(`/userword/${dbRoom.id}`)
          } else if (dbRoom.status === 'prompt_writing') {
            console.log('Navigating to game page')
            navigate(`/game/${dbRoom.id}`)
          } else if (dbRoom.status === 'voting') {
            console.log('Navigating to voting page')
            navigate(`/voting/${dbRoom.id}`)
          } else if (dbRoom.status === 'results') {
            // Only navigate to results if we're not already in lobby
            // This prevents automatic redirect when user manually returns to lobby after game
            const currentPath = window.location.pathname
            if (!currentPath.includes('/lobby/')) {
              console.log('Navigating to results page')
              navigate(`/results/${dbRoom.id}`)
            } else {
              console.log('Already in lobby, staying here despite results status')
            }
          } else if (dbRoom.status === 'waiting') {
            // Stay in lobby
            console.log('Staying in lobby')
            navigate(`/lobby/${dbRoom.id}`)
          }
        }
        loadRoom()
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'players',
        filter: `room_id=eq.${roomId}`
      }, (payload) => {
        // Host oyuncu satırı silinirse modal aç
        try {
          const ev: any = payload as any
          const wasDelete = ev.eventType === 'DELETE'
          const oldRow = (ev.old || {}) as any
          if (wasDelete && oldRow && oldRow.is_host && !amIHost()) {
            setShowRoomClosed(true)
            return
          }
        } catch {}
        loadRoom()
      })
      .subscribe()

    return () => {
      try {
        // Gracefully remove channel to avoid WebSocket errors
        supabase.removeChannel(channel)
      } catch (error) {
        // Ignore WebSocket cleanup errors
        console.log('WebSocket cleanup completed')
      }
    }
  }

  const setupPresence = async () => {
    if (!roomId) return
    const savedPlayer = localStorage.getItem('currentPlayer')
    const me = savedPlayer ? (JSON.parse(savedPlayer) as Player) : null
    if (!me) return

    await realtimeService.joinRoom(roomId, {
      id: me.id,
      name: me.name,
      isHost: !!me.isHost,
      isReady: !!me.isReady,
    })

    realtimeService.onPresenceSync(roomId, (users) => {
      const presentIds = new Set(users.map((u) => u.id))
      const merged = (room?.players || []).filter((p) => presentIds.has(p.id))
      setOnlinePlayers(merged)
      // Host çevrimdışı olabilir; artık modal göstermiyoruz (sadece online listesi güncellenir)
    })

    // Broadcast my arrival once per session (avoid duplicates)
    const broadcastKey = `joinedBroadcast:${roomId}`
    if (!sessionStorage.getItem(broadcastKey)) {
      await realtimeService.broadcast(roomId, 'player_joined', {
        player: me,
      })
      sessionStorage.setItem(broadcastKey, '1')
    }

    // Listen for fast UI updates via broadcast (db changes will still update as fallback)
    realtimeService.on(roomId, 'player_joined', ({ player }) => {
      setRoom((prev) => {
        if (!prev) return prev
        const exists = prev.players.some((p) => p.id === player.id)
        if (exists) return prev
        return { ...prev, players: [...prev.players, player] }
      })
    })

    // Remove player immediately when a leave broadcast is received
    realtimeService.on(roomId, 'player_left', ({ playerId }) => {
      setRoom((prev) => {
        if (!prev) return prev
        const wasHost = prev.players.some(p => p.id === playerId && p.isHost)
        if (wasHost && !currentPlayer?.isHost) {
          setShowRoomClosed(true)
        }
        return { ...prev, players: prev.players.filter(p => p.id !== playerId) }
      })
      setOnlinePlayers(prev => prev.filter(p => p.id !== playerId))
    })

    realtimeService.on(roomId, 'player_ready_toggled', ({ playerId, isReady }) => {
      setRoom((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          players: prev.players.map((p) => p.id === playerId ? { ...p, isReady } : p)
        }
      })
    })

    // Navigate all players when host starts, based on game mode
    realtimeService.on(roomId, 'host_start_game', ({ roomId: rid, gameMode }) => {
      if (!rid) return
      if (gameMode === 'user_word_to_prompt') {
        navigate(`/userword/${rid}`)
      } else {
        navigate(`/game/${rid}`)
      }
    })

    // Handle player kicked event
    realtimeService.on(roomId, 'player_kicked', ({ playerId }) => {
      // If current player is kicked, navigate to home
      if (playerId === me.id) {
        navigate('/')
        return
      }
      
      // Update room state to remove kicked player
      setRoom((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          players: prev.players.filter(p => p.id !== playerId)
        }
      })
    })
  }

  const saveSettings = async () => {
    if (!room || !currentPlayer?.isHost) return
    
    try {
      const gameModeChanged = editedGameMode !== room.gameMode
      await databaseService.updateRoomSettings(
        room.id, 
        room.hostId, 
        editedSettings,
        gameModeChanged ? editedGameMode : undefined
      )
      setEditingSettings(false)
      setError('')
      await loadRoom() // Reload to get updated data
    } catch (error: any) {
      setError(error.message || 'Ayarlar güncellenirken hata oluştu')
    }
  }

  const cancelEdit = () => {
    setEditingSettings(false)
    // Reset to current room settings
    if (room) {
      setEditedSettings({
        maxPlayers: room.maxPlayers,
        maxRounds: room.maxRounds,
        promptTimeLimit: room.settings.promptTimeLimit,
        votingTimeLimit: room.settings.votingTimeLimit
      })
      setEditedGameMode(room.gameMode)
    }
  }

  const copyRoomCode = async () => {
    if (!room?.code) return
    const roomCode = room.code
    try {
      await navigator.clipboard.writeText(roomCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea')
      textArea.value = roomCode
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const toggleReady = async () => {
    if (!currentPlayer) return

    try {
      const newReadyStatus = !currentPlayer.isReady
      await databaseService.updatePlayerReady(currentPlayer.id, newReadyStatus)
      
      setCurrentPlayer(prev => prev ? { ...prev, isReady: newReadyStatus } : null)
      // Update local room state immediately for instant UI feedback
      setRoom((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          players: prev.players.map(p => p.id === currentPlayer.id ? { ...p, isReady: newReadyStatus } : p)
        }
      })
      if (roomId) {
        await realtimeService.broadcast(roomId, 'player_ready_toggled', {
          playerId: currentPlayer.id,
          isReady: newReadyStatus,
        })
      }
    } catch (error: any) {
      setError(error.message || 'Hazır durumu güncellenirken hata oluştu')
    }
  }

  const startGame = async () => {
    if (!room || !currentPlayer?.isHost) return

    try {
      // If there is leftover data from a previous game, reset the room first
      const existing = await databaseService.hasExistingGameData(room.id)
      const shouldReset =
        room.status === 'results' ||
        room.status === 'finished' ||
        existing.hasPrompts || existing.hasRounds || existing.hasUserWords
      if (shouldReset) {
        await databaseService.resetRoomForRematch(room.id, currentPlayer.id)
      }

      // Set all non-host players to unready before starting
      await databaseService.unreadyAllNonHostPlayers(room.id)
      // Update local state immediately
      setRoom(prev => {
        if (!prev) return prev
        return {
          ...prev,
          players: prev.players.map(p => p.isHost ? p : { ...p, isReady: false })
        }
      })

      if (room.gameMode === 'user_word_to_prompt') {
        // For user_word_to_prompt mode, start with word submission phase
        await databaseService.startGameWithUserWords(room.id)
        // Broadcast precise round start time for timer sync
        if (roomId) {
          await realtimeService.broadcast(roomId, 'round_started', {
            roundNumber: 1,
            phase: 'word_submission',
            startedAt: Date.now(),
            timeLimitSec: 60, // 60 seconds for word submission
          })
        }
        if (roomId) {
          await realtimeService.broadcast(roomId, 'host_start_game', {
            roomId: room.id,
            gameMode: 'user_word_to_prompt',
          })
        }

        // Analytics: user_word_to_prompt modu icin oyun basladi
        trackEvent({
          event: 'scraible_game_start',
          properties: {
            room_id: room.id,
            game_mode: room.gameMode,
            max_rounds: room.maxRounds,
            players_count: room.players.length,
            mode: 'user_word_to_prompt',
          },
        })

        navigate(`/userword/${room.id}`)
      } else {
        // Original logic for word_to_prompt mode
        await databaseService.startGame(room.id)
        // Broadcast precise round start time for timer sync
        if (roomId) {
          await realtimeService.broadcast(roomId, 'round_started', {
            roundNumber: 1,
            phase: 'prompt_writing',
            startedAt: Date.now(),
            timeLimitSec: room.settings.promptTimeLimit,
          })
        }
        if (roomId) {
          await realtimeService.broadcast(roomId, 'host_start_game', {
            roomId: room.id,
            gameMode: 'word_to_prompt',
          })
        }

        // Analytics: klasik word_to_prompt modu icin oyun basladi
        trackEvent({
          event: 'scraible_game_start',
          properties: {
            room_id: room.id,
            game_mode: room.gameMode,
            max_rounds: room.maxRounds,
            players_count: room.players.length,
            mode: 'word_to_prompt',
          },
        })

        navigate(`/game/${room.id}`)
      }
    } catch (error: any) {
      setError(error.message || 'Oyun başlatılırken hata oluştu')
    }
  }

  const canStartGame = () => {
    if (!room || !currentPlayer?.isHost) return false
    return room.players.length >= 2 && room.players.every(p => p.isReady)
  }

  const kickPlayer = async (playerIdToKick: string) => {
    if (!room || !currentPlayer?.isHost || !roomId) return

    try {
      await databaseService.kickPlayer(roomId, playerIdToKick, currentPlayer.id)
      
      // Broadcast the kick event
      await realtimeService.broadcast(roomId, 'player_kicked', {
        playerId: playerIdToKick,
        kickedBy: currentPlayer.id,
      })
      
      setKickingPlayer(null) // Close confirmation dialog
    } catch (error: any) {
      setError(error.message || 'Kullanıcı atılırken hata oluştu')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-white/20 border-t-violet-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-2 border-purple-400/30 rounded-full animate-pulse mx-auto"></div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">{t('lobby.loadingTitle')}</h3>
            <p className="text-white/70">{t('lobby.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="card-gradient max-w-md mx-auto border-red-400/50 bg-red-500/10">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <X className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">{t('lobby.errorTitle')}</h2>
            <p className="text-white/80 mb-6">{error || (t('lobby.errorUnknown') as string)}</p>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              {t('lobby.backHome')}
            </motion.button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), title (center), spacer (right) */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={async () => {
                  try {
                    if (room && currentPlayer) {
                      await databaseService.leaveRoom(room.id, currentPlayer.id)
                      if (roomId) {
                        await realtimeService.broadcast(roomId, 'player_left', { playerId: currentPlayer.id })
                        // Clear session storage so player can rejoin properly
                        sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
                      }
                    }
                  } finally {
                    navigate('/')
                  }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.back')}</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <motion.h1 
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-xl font-bold text-gradient"
              >
                {room.name}
              </motion.h1>
            </div>
            <div className="flex-shrink-0">
              {room && room.status !== 'waiting' && room.status !== 'finished' && (
                <motion.button
                  onClick={() => {
                    if (!room) return
                    if (room.status === 'prompt_writing' || room.status === 'generating') navigate(`/game/${room.id}`)
                    else if (room.status === 'voting') navigate(`/voting/${room.id}`)
                    else if (room.status === 'results') navigate(`/results/${room.id}`)
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary px-3 py-2 text-xs"
                >
                  {room.status === 'prompt_writing' || room.status === 'generating' ? t('lobby.actions.resumeGame') : null}
                  {room.status === 'voting' ? t('lobby.actions.resumeVoting') : null}
                  {room.status === 'results' ? t('lobby.actions.goToResults') : null}
                </motion.button>
              )}
            </div>
          </div>
          <div className="mt-1 flex items-center justify-center space-x-2">
            <Sparkles className="w-3 h-3 text-violet-400" />
            <p className="text-white/80 font-medium text-xs">{t('lobby.pageTitle')}</p>
            <Sparkles className="w-3 h-3 text-violet-400" />
          </div>
        </div>

        {/* Desktop header keeps previous layout */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <motion.button
            onClick={async () => {
              try {
                if (room && currentPlayer) {
                  await databaseService.leaveRoom(room.id, currentPlayer.id)
                  if (roomId) {
                    await realtimeService.broadcast(roomId, 'player_left', { playerId: currentPlayer.id })
                    // Clear session storage so player can rejoin properly
                    sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
                  }
                }
              } finally {
                navigate('/')
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t('lobby.backHome')}</span>
          </motion.button>
          
          <div className="text-center flex-1">
            <motion.h1 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="text-3xl md:text-4xl font-bold text-gradient mb-2"
            >
              {room.name}
            </motion.h1>
            <div className="flex items-center justify-center space-x-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <p className="text-white/80 font-medium text-base">{t('lobby.pageTitle')}</p>
              <Sparkles className="w-5 h-5 text-violet-400" />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {room && room.status !== 'waiting' && room.status !== 'finished' && (
              <motion.button
                onClick={() => {
                  if (!room) return
                  if (room.status === 'prompt_writing' || room.status === 'generating') navigate(`/game/${room.id}`)
                  else if (room.status === 'voting') navigate(`/voting/${room.id}`)
                  else if (room.status === 'results') navigate(`/results/${room.id}`)
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                {room.status === 'prompt_writing' || room.status === 'generating' ? (
                  <>
                    <Play className="w-4 h-4" />
                    <span>{t('lobby.actions.resumeGame')}</span>
                  </>
                ) : null}
                {room.status === 'voting' ? (
                  <>
                    <Trophy className="w-4 h-4" />
                    <span>{t('lobby.actions.resumeVoting')}</span>
                  </>
                ) : null}
                {room.status === 'results' ? (
                  <>
                    <Crown className="w-4 h-4" />
                    <span>{t('lobby.actions.goToResults')}</span>
                  </>
                ) : null}
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Room Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-gradient"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">{t('lobby.roomInfo.title')}</h2>
              <p className="text-white/60 text-sm sm:text-base">{t('lobby.roomInfo.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-center sm:justify-end">
            {currentPlayer?.isHost && (
              <>
                {editingSettings ? (
                  <motion.button
                    onClick={saveSettings}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 sm:p-3 rounded-xl bg-green-500/20 hover:bg-green-500/30 border border-green-400/50 text-green-400 transition-all"
                    title="Ayarları Kaydet"
                  >
                    <Save className="w-5 h-5" />
                  </motion.button>
                ) : null}
                <motion.button
                  onClick={() => editingSettings ? cancelEdit() : setEditingSettings(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 sm:p-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
                  title={editingSettings ? "İptal Et" : "Ayarları Düzenle"}
                >
                  {editingSettings ? (
                    <X className="w-5 h-5 text-white" />
                  ) : (
                    <Edit className="w-5 h-5 text-white" />
                  )}
                </motion.button>
              </>
            )}
            
            <div className="bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2 rounded-xl border border-white/20 flex-1 sm:flex-none">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold font-mono text-white tracking-wider">
                  {room?.code ?? '------'}
                </div>
                <div className="text-xs text-white/60">{t('lobby.roomInfo.roomCode')}</div>
              </div>
            </div>
            <motion.button
              onClick={copyRoomCode}
              disabled={!room?.code}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2 sm:p-3 rounded-xl transition-all ${
                room?.code 
                  ? 'bg-white/10 hover:bg-white/20 border border-white/20' 
                  : 'opacity-50 cursor-not-allowed bg-white/5'
              }`}
              title={t('lobby.roomInfo.copyRoomCode')}
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-white" />
              )}
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
          {/* Max Players */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.players')}</div>
            {editingSettings ? (
              <select
                value={editedSettings.maxPlayers}
                onChange={(e) => setEditedSettings(prev => ({ ...prev, maxPlayers: parseInt(e.target.value) }))}
                className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {[2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n} className="bg-gray-800 text-white">{n}</option>
                ))}
              </select>
            ) : (
              <div className="text-white font-bold text-lg">{room.maxPlayers}</div>
            )}
          </motion.div>

          {/* Online Players */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Zap className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.online')}</div>
            <div className="text-white font-bold text-lg">{onlinePlayers.length}</div>
          </motion.div>

          {/* Max Rounds */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Target className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.rounds')}</div>
            {editingSettings ? (
              <select
                value={editedSettings.maxRounds}
                onChange={(e) => setEditedSettings(prev => ({ ...prev, maxRounds: parseInt(e.target.value) }))}
                className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n} className="bg-gray-800 text-white">{n}</option>
                ))}
              </select>
            ) : (
              <div className="text-white font-bold text-lg">{room.maxRounds}</div>
            )}
          </motion.div>

          {/* Game Mode */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Crown className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.mode')}</div>
            {editingSettings ? (
              <select
                value={editedGameMode}
                onChange={(e) => setEditedGameMode(e.target.value as 'word_to_prompt' | 'user_word_to_prompt')}
                className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-center text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="word_to_prompt" className="bg-gray-800 text-white">{t('lobby.stats.modeClassic')}</option>
                <option value="user_word_to_prompt" className="bg-gray-800 text-white">{t('lobby.stats.modeCreative')}</option>
              </select>
            ) : (
              <div className="text-white font-bold text-lg">
                {room.gameMode === 'user_word_to_prompt' ? t('lobby.stats.modeCreative') : t('lobby.stats.modeClassic')}
              </div>
            )}
          </motion.div>

          {/* Prompt Time */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Timer className="w-6 h-6 text-orange-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.prompt')}</div>
            {editingSettings ? (
              <div className="flex items-center justify-center space-x-1">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={Math.floor(editedSettings.promptTimeLimit / 60)}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value) || 1
                    const seconds = Math.max(60, Math.min(1800, minutes * 60)) // 1-30 dakika arası
                    setEditedSettings(prev => ({ ...prev, promptTimeLimit: seconds }))
                  }}
                  className="w-12 bg-white/10 border border-white/20 rounded-md px-1 py-1 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <span className="text-white text-sm">{t('common.minutesShort')}</span>
              </div>
            ) : (
              <div className="text-white font-bold text-lg">{Math.floor(room.settings.promptTimeLimit / 60)} {t('common.minutesShort')}</div>
            )}
          </motion.div>

          {/* Voting Time */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45 }}
            className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 text-center"
          >
            <Timer className="w-6 h-6 text-pink-400 mx-auto mb-2" />
            <div className="text-white/60 text-sm">{t('lobby.stats.voting')}</div>
            {editingSettings ? (
              <div className="flex items-center justify-center space-x-1">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={Math.floor(editedSettings.votingTimeLimit / 60)}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value) || 1
                    const seconds = Math.max(60, Math.min(1200, minutes * 60)) // 1-20 dakika arası
                    setEditedSettings(prev => ({ ...prev, votingTimeLimit: seconds }))
                  }}
                  className="w-12 bg-white/10 border border-white/20 rounded-md px-1 py-1 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
                <span className="text-white text-sm">{t('common.minutesShort')}</span>
              </div>
            ) : (
              <div className="text-white font-bold text-lg">{Math.floor(room.settings.votingTimeLimit / 60)} {t('common.minutesShort')}</div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Players List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-gradient"
      >
        <div className="flex items-center space-x-3 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{t('lobby.players.title')}</h2>
            <p className="text-white/60 text-sm sm:text-base">{t('lobby.players.inLobby', { count: room.players.length })}</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {room.players.map((player, index) => (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, x: -30, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.1, type: "spring", stiffness: 100 }}
              className={`relative overflow-hidden rounded-2xl border backdrop-blur-sm transition-all duration-300 hover:scale-105 ${
                player.id === currentPlayer?.id 
                  ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border-violet-400/50 shadow-lg shadow-violet-500/20' 
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between p-4 sm:p-6 gap-4">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="relative flex-shrink-0">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg ${
                      player.isHost 
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
                        : 'bg-gradient-to-br from-blue-500 to-purple-600'
                    }`}>
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    {player.isHost && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Crown className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                      <span className="font-bold text-white text-base sm:text-lg truncate">{player.name}</span>
                      {player.isHost && (
                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                          {t('lobby.players.host')}
                        </span>
                      )}
                      {player.id === currentPlayer?.id && (
                        <span className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                          {t('lobby.players.you')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <div className="flex items-center space-x-1">
                        <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 flex-shrink-0" />
                        <span className="text-white/80 text-xs sm:text-sm font-medium">{player.score} {t('results.points')}</span>
                      </div>
                      <div className={`flex items-center space-x-1 ${
                        onlinePlayers.some(p => p.id === player.id) ? 'text-green-400' : 'text-gray-400'
                      }`}>
                        <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${
                          onlinePlayers.some(p => p.id === player.id) ? 'bg-green-400' : 'bg-gray-400'
                        }`}></div>
                        <span className="text-xs font-medium">
                          {onlinePlayers.some(p => p.id === player.id) ? t('lobby.players.online') : t('lobby.players.offline')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0 justify-end">
                  <div className="flex items-center space-x-2">
                    {player.isReady ? (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 px-2 py-1.5 sm:px-3 sm:py-2 rounded-xl flex-shrink-0"
                      >
                        <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                        <span className="text-white text-xs sm:text-sm font-medium">{t('lobby.players.ready')}</span>
                      </motion.div>
                    ) : (
                      <div className="flex items-center space-x-2 bg-white/10 px-2 py-1.5 sm:px-3 sm:py-2 rounded-xl flex-shrink-0">
                        <UserX className="w-3 h-3 sm:w-4 sm:h-4 text-white/60" />
                        <span className="text-white/60 text-xs sm:text-sm font-medium">{t('lobby.players.waiting')}</span>
                      </div>
                    )}
                    
                    {/* Kick button - only show for host and not for themselves */}
                    {currentPlayer?.isHost && player.id !== currentPlayer.id && (
                      <motion.button
                        onClick={() => setKickingPlayer(player)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-1.5 sm:p-2 bg-red-500/20 hover:bg-red-500/40 rounded-xl transition-colors text-red-400 hover:text-red-300 border border-red-500/30 flex-shrink-0"
                        title={t('lobby.players.kickTitle', { name: player.name }) as string}
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="space-y-6"
      >
        {currentPlayer && !currentPlayer.isHost && (
          <motion.button
            onClick={toggleReady}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg ${
              currentPlayer.isReady
                ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700'
                : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-green-500/30'
            }`}
          >
            {currentPlayer.isReady ? (
              <>
                <UserX className="w-6 h-6" />
                <span>{t('lobby.actions.notReady')}</span>
              </>
            ) : (
              <>
                <UserCheck className="w-6 h-6" />
                <span>{t('lobby.actions.imReady')}</span>
              </>
            )}
          </motion.button>
        )}

        {currentPlayer?.isHost && (
          <div className="space-y-4">
            <motion.button
              onClick={startGame}
              disabled={!canStartGame()}
              whileHover={{ scale: canStartGame() ? 1.02 : 1 }}
              whileTap={{ scale: canStartGame() ? 0.98 : 1 }}
              className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg ${
                canStartGame()
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 shadow-violet-500/30 neon-glow'
                  : 'bg-gray-500/20 text-white/50 cursor-not-allowed border border-white/10'
              }`}
            >
              <Play className="w-6 h-6" />
              <span>{t('lobby.actions.startGame')}</span>
              <Sparkles className="w-5 h-5" />
            </motion.button>

            {!canStartGame() && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="card-gradient border-amber-400/30 bg-amber-500/10 text-center"
              >
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-semibold">{t('lobby.actions.requirements')}</span>
                </div>
                <p className="text-white/80">
                  {room.players.length < 2 
                    ? t('lobby.actions.needTwoPlayers')
                    : t('lobby.actions.needAllReady')
                  }
                </p>
              </motion.div>
            )}
          </div>
        )}

        {!currentPlayer?.isHost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="card-gradient border-blue-400/30 bg-blue-500/10">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="text-blue-400 font-semibold">{t('lobby.actions.waitingForHost')}</span>
              </div>
              <p className="text-white/80">
                {t('lobby.actions.hostWillStart')}
              </p>
            </div>
          </motion.div>
        )}
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="card-gradient border-red-400/50 bg-red-500/10"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-red-400 font-medium">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Kick Confirmation Dialog */}
      {kickingPlayer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 safe-area"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className="card-gradient max-w-md w-full border-red-400/50"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Kullanıcı Atma Onayı
              </h3>
              <p className="text-white/80">
                <strong className="text-red-400">{kickingPlayer.name}</strong> kullanıcısını odadan atmak istediğinizden emin misiniz?
              </p>
            </div>
            
            <div className="flex space-x-4">
              <motion.button
                onClick={() => setKickingPlayer(null)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all"
              >
                İptal
              </motion.button>
              <motion.button
                onClick={() => kickPlayer(kickingPlayer.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all shadow-lg"
              >
                Kullanıcıyı At
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Room Closed Modal */}
      <RoomClosedModal
        isOpen={showRoomClosed}
        onClose={() => {
          try {
            if (roomId) {
              realtimeService.cleanupRoom(roomId)
              sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
            }
          } finally {
            navigate('/')
          }
        }}
      />
    </div>
  )
}
